import React, { useState, useEffect, useMemo } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetSalarySummaryBulkQuery } from "../../../utils/redux/api/financeApi";

interface EmployeeSalariesProps {
  clientId: string;
  selectedMonths: string[];
  availableLocations: { _id: string }[];
  locationFilters: string[];
  sectorFilters?: string[];
  onLoadingChange?: (loading: boolean) => void;
}

interface EmployeeEarnings {
  byLocation: Record<string, { locationName: string; total: number; employees: { name: string; total: number; userId: string }[] }>;
  bySector: Record<string, { sectorName: string; total: number; employees: { name: string; total: number; userId: string; locationName?: string }[] }>;
}

const AVATAR_COLORS = [
  "#6366f1","#0ea5e9","#16a34a","#ea580c",
  "#9333ea","#dc2626","#0891b2","#b45309","#4f46e5",
];

function avatarColor(index: number) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

const fmt = (n: number) =>
  new Intl.NumberFormat("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);

export const EmployeeSalaries: React.FC<EmployeeSalariesProps> = ({
  clientId,
  selectedMonths,
  availableLocations,
  locationFilters,
  sectorFilters = [],
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<"location" | "sector">("location");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const locationsKey = locationFilters.sort().join(",");
  const sectorsKey   = sectorFilters.sort().join(",");
  const availableLocationsKey = availableLocations.map((l) => l._id).sort().join(",");

  const sortedMonths = [...selectedMonths].sort();
  const startDate = sortedMonths.length
    ? dayjs(sortedMonths[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD")
    : "";
  const endDate = sortedMonths.length
    ? dayjs(sortedMonths[sortedMonths.length - 1], "YYYY-MM").endOf("month").format("YYYY-MM-DD")
    : "";

  const { data, isLoading: loading } = useGetSalarySummaryBulkQuery(
    { clientId, startDate, endDate },
    { skip: !clientId || selectedMonths.length === 0 },
  );

  useEffect(() => { onLoadingChange?.(loading); }, [loading, onLoadingChange]);

  const earnings: EmployeeEarnings = useMemo(() => {
    let summaries = data?.summaries || [];

    const allowedLocationIds =
      locationFilters.length > 0 ? locationFilters : availableLocations.map((l) => l._id);
    summaries = summaries.filter((s: any) => {
      const loc = s.user?.location?._id || s.user?.location;
      return loc && allowedLocationIds.includes(loc.toString());
    });
    if (sectorFilters.length > 0) {
      summaries = summaries.filter((s: any) => {
        const sid = s.user?.positionInSector?.[0]?.workSector?._id || s.user?.positionInSector?.[0]?.workSector;
        return sid && sectorFilters.includes(sid.toString());
      });
    }

    const byLocation: EmployeeEarnings["byLocation"] = {};
    const bySector: EmployeeEarnings["bySector"]   = {};

    summaries.forEach(({ user, salarySummary }: any) => {
      const total   = salarySummary?.totalForMonth ?? 0;
      const locId   = user?.location?._id || "no-location";
      const locName = user?.location?.name || t("No location");
      const name    = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || t("Unknown");

      if (!byLocation[locId]) byLocation[locId] = { locationName: locName, total: 0, employees: [] };
      byLocation[locId].total += total;
      byLocation[locId].employees.push({ name, total, userId: user?._id });

      const sectorId   = user?.positionInSector?.[0]?.workSector?._id || "no-sector";
      const sectorName = user?.positionInSector?.[0]?.workSector?.name || t("No sector");
      if (!bySector[sectorId]) bySector[sectorId] = { sectorName, total: 0, employees: [] };
      bySector[sectorId].total += total;
      bySector[sectorId].employees.push({ name, total, userId: user?._id, locationName: locName });
    });

    return { byLocation, bySector };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, locationsKey, sectorsKey, availableLocationsKey, t]);

  const getPeriodLabel = () => {
    if (selectedMonths.length === 0) return "";
    const sorted = [...selectedMonths].sort();
    if (sorted.length === 1) return dayjs(sorted[0], "YYYY-MM").format("MMMM YYYY");
    return `${dayjs(sorted[0], "YYYY-MM").format("MMM YYYY")} – ${dayjs(sorted[sorted.length - 1], "YYYY-MM").format("MMM YYYY")}`;
  };

  const groups = viewMode === "location"
    ? Object.entries(earnings.byLocation).map(([id, g]) => ({ id, name: g.locationName, total: g.total, employees: g.employees }))
    : Object.entries(earnings.bySector).map(([id, g]) => ({ id, name: g.sectorName, total: g.total, employees: g.employees }));

  const grandTotal = Object.values(earnings.byLocation).reduce((sum, g) => sum + g.total, 0);

  const PREVIEW_COUNT = 5;

  return (
    <section className="fin-section">
      <div className="fs-head">
        <div>
          <h2>{t("Employee Salaries")}</h2>
          <div className="fs-sub">{t("Total by locations and sectors")} · <span style={{ fontFamily: "monospace" }}>{getPeriodLabel()}</span></div>
        </div>
        <div className="fs-toolbar">
          <div className="fs-grand-total">
            <span className="fs-grand-total-lbl">{t("Total")} ({t("company expense")})</span>
            <span className="fs-grand-total-val">{fmt(grandTotal)} RSD</span>
          </div>
          <div className="fin-seg">
            <button className={viewMode === "location" ? "active" : ""} onClick={() => setViewMode("location")}>{t("Locations")}</button>
            <button className={viewMode === "sector"   ? "active" : ""} onClick={() => setViewMode("sector")}>{t("Sectors")}</button>
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        {groups.length === 0 && !loading ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
            {t("No employee salary data available")}
          </div>
        ) : (
          <div className="loc-grid">
            {groups.map((group, gi) => {
              const sorted  = [...group.employees].sort((a, b) => b.total - a.total);
              const isZero  = group.total === 0;
              const isExpanded = !!expanded[group.id];
              const visible = isExpanded ? sorted : sorted.slice(0, PREVIEW_COUNT);
              const hasMore = sorted.length > PREVIEW_COUNT;

              return (
                <div key={group.id} className={`loc-card${isZero ? " zero" : ""}`}>
                  <div className="loc-card-head">
                    <span className="loc-card-name">{group.name}</span>
                    <span className="loc-card-total">{fmt(group.total)} RSD</span>
                  </div>

                  <div className="loc-rows">
                    {visible.map((emp, ei) => (
                      <div key={emp.userId || ei} className={`loc-row${emp.total === 0 ? " zero" : ""}`}>
                        <span
                          className="loc-row-ava"
                          style={{ background: isZero ? "#9ca3af" : avatarColor(gi * 10 + ei) }}
                        >
                          {initials(emp.name)}
                        </span>
                        <span className="loc-row-nm" title={emp.name}>{emp.name}</span>
                        <span className="loc-row-am">
                          {fmt(emp.total)} <span className="cur">RSD</span>
                        </span>
                      </div>
                    ))}
                  </div>

                  {hasMore && (
                    <div className="loc-card-foot">
                      <button
                        className="loc-show-all"
                        onClick={() => setExpanded((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                      >
                        {isExpanded ? t("Show less") : `${t("Show all")} (${sorted.length})`}
                        <svg
                          width="11" height="11" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          style={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
                        >
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Spin>
    </section>
  );
};
